import * as React from 'react';

import { useParams } from 'react-router';

import { usePublicDoc } from '@/mock';
import type { PublicDoc, RichBlock, RichDoc } from '@/mock';
import { previewKind } from '@/shared/attachment-preview/preview-kind';
import { Link2OffIcon, SnowflakeIcon } from '@/shared/icon-library';
import { RichDocView } from '@/shared/rich-editor/rich-doc-view';

import './public-doc.css';

const DATE_FORMAT = new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });
const SIZE_FORMAT = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 1 });
const SIZE_UNITS = ['Б', 'КБ', 'МБ', 'ГБ'];
const SIZE_STEP = 1024;

function formatDate(ms: number): string {
  return DATE_FORMAT.format(ms).replace(/\s*г\.$/, '');
}

function formatSize(bytes: number): string {
  let value = bytes;
  let unit = 0;
  while (value >= SIZE_STEP && unit < SIZE_UNITS.length - 1) {
    value /= SIZE_STEP;
    unit += 1;
  }
  return `${SIZE_FORMAT.format(value)} ${SIZE_UNITS[unit]}`;
}

function isInlineMedia(file: PublicDoc['files'][string]): boolean {
  const kind = previewKind(file.mime, file.name);
  return kind === 'image' || kind === 'video' || kind === 'audio';
}

function withFileSizes(doc: RichDoc, files: PublicDoc['files']): RichDoc {
  return doc.map((block): RichBlock => {
    const file = block.type === 'file' ? files[String(block.props['attachmentId'] ?? '')] : undefined;
    const props =
      file && !isInlineMedia(file) ? { ...block.props, name: `${file.name} · ${formatSize(file.sizeBytes)}` } : block.props;
    return { ...block, props, children: withFileSizes(block.children, files) };
  });
}

function useDocumentTitle(title: string | null): void {
  React.useEffect(() => {
    if (title === null) return undefined;
    const previous = document.title;
    document.title = title;
    return () => {
      document.title = previous;
    };
  }, [title]);
}

function Frame({ children }: { children?: React.ReactNode }) {
  return (
    <div className="public-doc">
      <header className="public-doc__bar">
        <div className="public-doc__column">
          <div className="public-doc__brand">
            <SnowflakeIcon aria-hidden />
            <span>База знаний FrostPod</span>
          </div>
        </div>
      </header>
      {children}
    </div>
  );
}

function Missing() {
  return (
    <main className="public-doc__page public-doc__column">
      <div className="public-doc__missing">
        <Link2OffIcon aria-hidden />
        <h1>Документ недоступен по ссылке</h1>
        <p>Возможно, доступ по ссылке закрыли или документ убрали в архив. Попросите владельца прислать новую ссылку.</p>
      </div>
    </main>
  );
}

function Article({ doc }: { doc: PublicDoc }) {
  const body = React.useMemo(() => withFileSizes(doc.bodyJson, doc.files), [doc]);
  const fileUrl = React.useCallback((attachmentId: string) => doc.files[attachmentId]?.url ?? null, [doc]);
  const owner = doc.ownerName === '' ? '' : `${doc.ownerName} · `;
  return (
    <>
      <main className="public-doc__page public-doc__column">
        <article>
          {doc.cover === null ? null : (
            <img src={doc.cover.url} alt="" className="public-doc__cover" />
          )}
          <h1 className="public-doc__title">{doc.title}</h1>
          <p className="public-doc__meta">
            {owner}обновлено {formatDate(doc.updatedAt)}
          </p>
          <RichDocView value={body} fileUrl={fileUrl} className="public-doc__body" />
        </article>
      </main>
      <footer className="public-doc__footer">
        <div className="public-doc__column">
          <p>Опубликовано через FrostPod · только для чтения</p>
        </div>
      </footer>
    </>
  );
}

export function PublicDocPage() {
  const { token = '' } = useParams();
  const doc = usePublicDoc(token);
  useDocumentTitle(doc === 'loading' || doc === null ? null : doc.title);
  if (doc === 'loading') return <div className="public-doc" />;
  return <Frame>{doc === null ? <Missing /> : <Article doc={doc} />}</Frame>;
}
